<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { api } from "./api";

type MenuItem = { path?: string; label: string; children?: Array<{ path: string; label: string }> };
type MenuGroup = { label: string; mark: string; items: MenuItem[] };

const route = useRoute();
const title = computed(() => String(route.meta.title ?? "工作台"));
const section = computed(() => String(route.meta.section ?? "工作台"));
const isPublic = computed(() => Boolean(route.meta.public));
const isEmbeddedApp = computed(() => route.path.startsWith("/apps/customer-service") || route.path.startsWith("/apps/store"));
const displayName = ref("当前用户");
const sidebarOpen = ref(false);
const expandedMenus = ref(new Set<string>());

const menuGroups: MenuGroup[] = [
  { label: "工作台", mark: "01", items: [
    { path: "/", label: "我的工作台" },
    { path: "/tasks", label: "我的待办" },
    { path: "/alerts", label: "异常提醒" }
  ] },
  { label: "业务应用", mark: "02", items: [
    { path: "/integrations", label: "应用中心" },
    { path: "/apps/store", label: "小程序商城" },
    { label: "客服工作台", children: [
      { path: "/apps/customer-service/online/current", label: "在线客服" },
      { path: "/apps/customer-service/manage/workgroups", label: "客服管理" }
    ] }
  ] },
  { label: "产品中心", mark: "03", items: [
    { path: "/products", label: "产品台账" },
    { path: "/product-initiatives", label: "新品立项" },
    { path: "/lifecycle", label: "生命周期看板" },
    { path: "/sku-batches", label: "SKU 与生产批次" }
  ] },
  { label: "营销中心", mark: "04", items: [
    { path: "/marketing/plans", label: "宣发计划" },
    { path: "/marketing/calendar", label: "任务日历" },
    { path: "/marketing/assets", label: "素材与参考链接" },
    { path: "/marketing/templates", label: "模板管理" }
  ] },
  { label: "审批与费用", mark: "05", items: [
    { path: "/approvals", label: "待我审批" },
    { path: "/applications", label: "我的申请" },
    { path: "/expenses", label: "费用台账" },
    { path: "/budgets", label: "预算" }
  ] },
  { label: "经营分析", mark: "06", items: [
    { path: "/analysis/profit", label: "产品毛利" },
    { path: "/analysis/sales", label: "销售与退款" },
    { path: "/analysis/costs", label: "成本待确认" }
  ] },
  { label: "系统管理", mark: "07", items: [
    { path: "/system/staff", label: "组织与职员" },
    { path: "/system/roles", label: "角色权限" },
    { path: "/system/workflows", label: "审批流程" },
    { path: "/system/integration-logs", label: "集成与日志" }
  ] }
];

function toggleMenu(label: string) {
  const next = new Set(expandedMenus.value);
  if (next.has(label)) next.delete(label);
  else next.add(label);
  expandedMenus.value = next;
}

watch(() => route.path, (path) => {
  if (path.startsWith("/apps/customer-service")) {
    expandedMenus.value = new Set([...expandedMenus.value, "客服工作台"]);
  }
}, { immediate: true });

onMounted(async () => {
  if (isPublic.value) return;
  try {
    const current = await api.me();
    displayName.value = String(current.displayName ?? current.username ?? "当前用户");
  } catch {
    // The API layer handles an expired session by redirecting to login.
  }
});

async function logout() {
  await api.logout();
  location.assign("/login");
}
</script>

<template>
  <RouterView v-if="isPublic" />
  <div v-else class="app-shell" :class="{ 'sidebar-open': sidebarOpen }">
    <aside class="sidebar">
      <div class="brand">
        <img class="brand-logo" src="/brand/workbench-logo-rounded-v2.png" alt="玩具百科 我的玩具库" />
        <div><strong>玩具百科</strong><small>一体化工作台</small></div>
      </div>
      <div class="entity-card">
        <span class="entity-dot"></span>
        <div><small>当前经营主体</small><strong>玩具百科</strong></div>
        <span class="entity-tag">主</span>
      </div>
      <nav class="side-nav">
        <section v-for="group in menuGroups" :key="group.label" class="nav-group">
          <div class="nav-group-title"><span>{{ group.mark }}</span>{{ group.label }}</div>
          <template v-for="item in group.items" :key="item.path ?? item.label">
            <button
              v-if="item.children"
              class="nav-parent"
              :class="{ active: route.path.startsWith('/apps/customer-service') }"
              type="button"
              :aria-expanded="expandedMenus.has(item.label)"
              @click="toggleMenu(item.label)"
            >
              <span>{{ item.label }}</span><b>{{ expandedMenus.has(item.label) ? '−' : '＋' }}</b>
            </button>
            <div v-if="item.children" v-show="expandedMenus.has(item.label)" class="nav-children">
              <RouterLink
                v-for="child in item.children"
                :key="child.path"
                :to="child.path"
                @click="sidebarOpen = false"
              >{{ child.label }}</RouterLink>
            </div>
            <RouterLink
              v-else-if="item.path"
              :to="item.path"
              @click="sidebarOpen = false"
            >{{ item.label }}</RouterLink>
          </template>
        </section>
      </nav>
      <div class="sidebar-foot"><span class="online-dot"></span>预览环境运行中</div>
    </aside>

    <main class="workspace">
      <header class="topbar">
        <button class="mobile-menu" type="button" aria-label="展开导航" @click="sidebarOpen = !sidebarOpen">☰</button>
        <div class="global-search">
          <span>⌕</span>
          <input aria-label="全局搜索" placeholder="搜索产品编号、SKU、费用或审批单号" />
          <kbd>⌘ K</kbd>
        </div>
        <div class="topbar-actions">
          <el-dropdown trigger="click">
            <button class="quick-create" type="button"><b>＋</b> 快捷新建</button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item disabled>新品立项 · 待接入</el-dropdown-item>
                <el-dropdown-item><RouterLink to="/expenses/new">支出申请</RouterLink></el-dropdown-item>
                <el-dropdown-item disabled>员工报销 · 待接入</el-dropdown-item>
                <el-dropdown-item disabled>营销任务 · 待接入</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <button class="icon-button" type="button" title="待办通知"><span>◔</span><i>0</i></button>
          <el-dropdown trigger="click">
            <button class="app-switch" type="button">应用切换 <span>⌄</span></button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item><RouterLink to="/integrations">查看应用接入状态</RouterLink></el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-dropdown trigger="click">
            <button class="account-button" type="button"><span class="avatar">{{ displayName.slice(0, 1) }}</span><span>{{ displayName }}</span><b>⌄</b></button>
            <template #dropdown>
              <el-dropdown-menu><el-dropdown-item @click="logout">退出登录</el-dropdown-item></el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <div v-if="!isEmbeddedApp" class="page-heading">
        <div>
          <div class="breadcrumb"><span>{{ section }}</span><b>/</b><span>{{ title }}</span></div>
          <h1>{{ title }}</h1>
        </div>
        <div class="environment-pill"><span></span>预览版 · 外部数据待接入</div>
      </div>
      <section class="content" :class="{ 'embedded-app-content': isEmbeddedApp }"><RouterView /></section>
    </main>
    <button v-if="sidebarOpen" class="sidebar-mask" type="button" aria-label="关闭导航" @click="sidebarOpen = false"></button>
  </div>
</template>
